import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Returns baby age as a human-readable string using floor-to-nearest-15-days logic.
 * - Under 1 month: "X days old"
 * - 1–11 months: "4 months old" or "4.5 months old" (half-month if ≥15 days into the month)
 * - 12+ months: "1 year old", "1y 3m old"
 * Handles Date, Firestore Timestamp, or ISO string.
 */
export function formatBabyAge(dob: any): string {
  let d: Date;
  if (dob && typeof dob.toDate === 'function') {
    d = dob.toDate();
  } else if (dob instanceof Date) {
    d = dob;
  } else {
    d = new Date(dob);
  }

  const now = new Date();
  const totalDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));

  // Under 30 days — show days
  if (totalDays < 30) {
    return `${totalDays} day${totalDays !== 1 ? 's' : ''} old`;
  }

  // Completed full months (day-of-month aware)
  let fullMonths = (now.getFullYear() - d.getFullYear()) * 12 + (now.getMonth() - d.getMonth());
  if (now.getDate() < d.getDate()) fullMonths -= 1;

  if (fullMonths >= 12) {
    const years = Math.floor(fullMonths / 12);
    const months = fullMonths % 12;
    if (months === 0) return `${years} year${years !== 1 ? 's' : ''} old`;
    return `${years}y ${months}m old`;
  }

  // Days elapsed since the last month birthday
  const lastMonthBirthday = new Date(d);
  lastMonthBirthday.setFullYear(now.getFullYear());
  lastMonthBirthday.setMonth(now.getMonth() - (now.getDate() < d.getDate() ? 1 : 0));
  lastMonthBirthday.setDate(d.getDate());
  const daysIntoMonth = Math.floor((now.getTime() - lastMonthBirthday.getTime()) / (1000 * 60 * 60 * 24));

  if (daysIntoMonth >= 15) {
    return `${fullMonths}.5 months old`;
  }
  return `${fullMonths} month${fullMonths !== 1 ? 's' : ''} old`;
}
